import React from 'react';
import { Menu } from 'antd';

import type { SupportedLanguage } from '../../../translate';
import type {
  EpiDocumentType,
  EpiPredefinedEntry,
  EpiSectionNode,
} from '../types/types';
import { buildDropdownItems } from './dropdownMenuBuilder';
import MenuItemTitle from '../components/MenuItemTitle';
import { sectionLabel } from './epiHelpers';

interface IGenerateMenuItemsParams {
  nodes: EpiSectionNode[];
  docType: EpiDocumentType;
  lang: SupportedLanguage;
  onClick: (node: EpiSectionNode) => void;
  onAddOptional: (
    parentPath: string,
    key: string,
    repeatable: boolean,
    title: string
  ) => void;
  onAddCustom: (parentPath: string, title: string) => void;
  onAddPredefined?: (host: EpiSectionNode, entry: EpiPredefinedEntry) => void;
  keepCustomSection?: (path: string) => void;
  onAddBlockAfter?: (blockPath: string, key: string) => void;
  editNatureType?: (node: EpiSectionNode) => void;
  moveCustomSection?: (path: string, direction: -1 | 1) => void;
  removeSection: (
    path: string,
    key: string,
    isCustom: boolean,
    customSectionId?: string
  ) => void;
  prefix: string;
  /** View-only: render the tree without any action dropdowns. */
  readOnly?: boolean;
  canAddCustomSections?: boolean;
}

export const generateMenuItems = ({
  nodes,
  docType,
  lang,
  onClick,
  onAddOptional,
  onAddCustom,
  onAddPredefined,
  keepCustomSection,
  onAddBlockAfter,
  editNatureType,
  moveCustomSection,
  removeSection,
  prefix,
  readOnly,
  canAddCustomSections,
}: IGenerateMenuItemsParams): React.ReactNode[] => {
  return nodes.map(node => {
    const key = `${prefix}${node.path}`;
    const dropdown = readOnly
      ? undefined
      : buildDropdownItems(node, {
          docType,
          lang,
          onAddOptional,
          onAddCustom,
          onAddPredefined,
          keepCustomSection,
          onAddBlockAfter,
          editNatureType,
          moveCustomSection,
          removeSection,
          canAddCustomSections,
          // `nodes` at this recursion level is the sibling set, in print order —
          // the remove gate for repeatable blocks counts instances of a key in
          // it, and Move up / Move down find their edges in it.
          siblings: nodes,
        });

    const title = (
      <MenuItemTitle
        title={sectionLabel(node.title)}
        // Repeatable instances share a title; the nature/type tells them
        // apart, so it gets its own line rather than the tail of a truncated
        // one.
        subtitle={node.natureType}
        unresolved={node.titleUnresolved}
        unmatched={node.unmatched}
        unmatchedBelow={node.unmatchedBelow}
        dropdownItems={dropdown}
        onClick={() => onClick(node)}
      />
    );

    if (node.children.length > 0) {
      return (
        <Menu.SubMenu
          key={key}
          title={title}
          onTitleClick={() => onClick(node)}
        >
          {generateMenuItems({
            nodes: node.children,
            docType,
            lang,
            onClick,
            onAddOptional,
            onAddCustom,
            onAddPredefined,
            keepCustomSection,
            onAddBlockAfter,
            editNatureType,
            moveCustomSection,
            removeSection,
            prefix,
            readOnly,
            canAddCustomSections,
          })}
        </Menu.SubMenu>
      );
    }

    return (
      <Menu.Item key={key} onClick={() => onClick(node)}>
        {title}
      </Menu.Item>
    );
  });
};
